// ListingCardGrid.tsx
import { useState } from "react";
import { motion } from "framer-motion";
import {
  Home,
  Building2,
  Clock,
  Calendar,
  MapPin,
  Star,
  Heart,
  Share2,
  Wind,
  Tv,
  Wifi,
  Coffee,
  Car,
  Users,
  Briefcase,
  Eye,
  Shield,
  Droplet,
  Dumbbell,
  Waves,
  Sofa,
  Dog,
  Zap,
  BookOpen,
  Umbrella,
  Leaf,
  ArrowUp,
  DoorOpenIcon,
} from "lucide-react";
import timeAgo from "@/helpers/dateTimeHelpers";

// Updated interface matching database schema
interface Listing {
  product_id: string;
  title: string;
  price: number;
  price_unit: "month" | "night" | "hour" | "day" | "total";
  location?: string;
  category: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  island?: string;
  timeAgo?: string;
  type: "physical" | "digital" | "service" | "rental";
  property_category: "residential" | "commercial";
  property_type: "apartment" | "room" | "office" | "shop" | "villa" | "commercial" | "house" | "land";
  property_status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  bedrooms?: number;
  bathrooms?: number;
  area_sqft?: number;
  area_sqm?: number;
  amenities: string[];
  images?: string[];
  image_url?: string;
  gallery?: string;
  rating?: number;
  reviews?: number;
  is_new: number; // 0 or 1
  is_featured: number; // 0 or 1
  created_at: string;
  updated_at: string;
  viewing_count: number;
  favorite_count: number;
  furnished?: "unfurnished" | "semi-furnished" | "fully-furnished";
  parking_spots: number;
  pet_friendly: number;
  immediate_available: number;
  utilities_included: number;
}

interface ListingCardGridProps {
  listing: Listing;
  onViewDetails?: (listing: Listing) => void;
  onLike?: (listing: Listing) => void;
}

// Complete amenity icons mapping
const amenityIcons: Record<string, any> = {
  // Cooling
  "Air Conditioning": Wind,
  AC: Wind,
  
  // Internet
  WiFi: Wifi,
  Wifi: Wifi,
  
  // Entertainment
  TV: Tv,
  
  // Parking
  Parking: Car,
  
  // Kitchen
  Kitchen: Home,
  "Kitchen Access": Home,
  
  // Laundry
  "Washing Machine": Home,
  Dryer: Home,
  
  // Fitness
  Gym: Dumbbell,
  
  // Recreation
  Pool: Waves,
  
  // Bathroom
  "Hot Water": Droplet,
  
  // Accessibility
  Elevator: ArrowUp,
  "Wheelchair Access": Users,
  
  // Security
  Security: Shield,
  
  // Outdoor
  Balcony: Home,
  Garden: Leaf,
  "Beach Access": Umbrella,
  
  // Furniture
  Furnished: Sofa,
  "Study Desk": BookOpen,
  
  // Pets
  "Pets Allowed": Dog,
  
  // Smoking
  "Smoking Allowed": Coffee,
  
  // Views
  "Ocean View": Eye,
  "City View": Eye,
  
  // Access
  "Private Entrance": DoorOpenIcon,
  
  // Utility
  "Backup Generator": Zap,
  "Water Tank": Droplet,
  
  // Commercial
  Workspace: Briefcase,
  "Display Area": Eye,
  Storage: Home,
  "Water Heater": Droplet,
  Restaurant: Coffee,
  "Mini Fridge": Home,
  "Private Bathroom": Home,
  Spa: Users,
};

export default function ListingCardGrid({ listing, onViewDetails, onLike }: ListingCardGridProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const formatPrice = () => {
    const price = listing.price?.toLocaleString() || "0";
    let unitText = "";
    switch (listing.price_unit) {
      case "month":
        unitText = "/month";
        break;
      case "night":
        unitText = "/night";
        break;
      case "hour":
        unitText = "/hour";
        break;
      case "day":
        unitText = "/day";
        break;
      case "total":
        unitText = "";
        break;
      default:
        unitText = "";
    }
    return `MVR ${price}${unitText}`;
  };

  const getStatusColor = () => {
    switch (listing.property_status) {
      case "available":
        return "bg-green-500";
      case "rented":
        return "bg-red-500";
      case "maintenance":
        return "bg-orange-500";
      case "coming_soon":
        return "bg-yellow-500";
      case "sold":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = () => {
    switch (listing.property_status) {
      case "available":
        return "Available";
      case "rented":
        return "Rented";
      case "maintenance":
        return "Under Maintenance";
      case "coming_soon":
        return "Coming Soon";
      case "sold":
        return "Sold";
      default:
        return listing.property_status;
    }
  };

  const getTypeIcon = () => {
    // Determine display type for UI
    if (listing.property_category === "commercial") {
      return <Building2 className="w-4 h-4" />;
    } else if (listing.price_unit === "night" || listing.price_unit === "hour" || listing.price_unit === "day") {
      return <Clock className="w-4 h-4" />;
    } else {
      return <Home className="w-4 h-4" />;
    }
  };

  const getDisplayType = () => {
    if (listing.property_category === "commercial") {
      return "Commercial";
    } else if (listing.price_unit === "night" || listing.price_unit === "hour" || listing.price_unit === "day") {
      return "Short Stay";
    } else {
      return "Residential";
    }
  };

  const getLocation = () => {
    return listing.address_line1 || listing.location || `${listing.island || ""} ${listing.city || ""}`.trim() || "Location not specified";
  };

  const getImageUrl = () => {
    // Priority: image_url > first image from gallery > null
    if (listing.image_url) return listing.image_url;
    if (listing.gallery) {
      try {
        const gallery = JSON.parse(listing.gallery);
        if (gallery && gallery[0]) return gallery[0];
      } catch (e) {
        return null;
      }
    }
    if (listing.images && listing.images[0]) return listing.images[0];
    return null;
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike?.(listing);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing.title,
          text: `Check out this ${getDisplayType().toLowerCase()} property`,
          url: `${window.location.origin}/listings/${listing.product_id}`,
        });
      } catch (error) {
        console.log("Share cancelled");
      }
    } else {
      await navigator.clipboard.writeText(`${window.location.origin}/listings/${listing.product_id}`);
      alert("Link copied to clipboard!");
    }
  };

  const handleClick = () => {
    onViewDetails?.(listing);
  };

  const imageUrl = getImageUrl();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleClick}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
    >
      {/* Image Section */}
      <div className="relative h-48 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {getTypeIcon()}
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {listing.is_new === 1 && (
            <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-lg">
              New
            </span>
          )}
          {listing.is_featured === 1 && (
            <span className="px-2 py-1 text-xs font-medium bg-yellow-500 text-white rounded-lg">
              Featured
            </span>
          )}
          {listing.price_unit === "night" && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-lg flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Nightly
            </span>
          )}
          {listing.price_unit === "hour" && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-lg flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Hourly
            </span>
          )}
          {listing.immediate_available === 1 && (
            <span className="px-2 py-1 text-xs font-medium bg-green-500 text-white rounded-lg">
              Move-in Ready
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={handleLike}
            className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors backdrop-blur-sm"
            aria-label="Like"
          >
            <Heart 
              className={`w-4 h-4 transition-colors ${
                isLiked 
                  ? "fill-red-500 text-red-500" 
                  : "text-gray-600 dark:text-gray-400 hover:text-red-500"
              }`} 
            />
          </button>
          <button
            onClick={handleShare}
            className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors backdrop-blur-sm"
            aria-label="Share"
          >
            <Share2 className="w-4 h-4 text-gray-600 dark:text-gray-400 hover:text-purple-500" />
          </button>
        </div>

        {/* Hover Overlay */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'} bg-black/50`}>
          <button className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors">
            Quick View
          </button>
        </div>

        {/* Type Badge */}
        <div className="absolute bottom-3 left-3 px-2 py-1 bg-white/90 dark:bg-gray-800/90 rounded-lg backdrop-blur-sm text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
          {getTypeIcon()}
          <span>{listing.category}</span>
        </div>

        {/* Bed/Bath Info */}
        {(listing.bedrooms !== undefined || listing.bathrooms !== undefined) && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-white/90 dark:bg-gray-800/90 rounded-lg backdrop-blur-sm text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            {listing.bedrooms !== undefined && listing.bedrooms > 0 && (
              <span className="flex items-center gap-1">
                <Home className="w-3 h-3" />
                {listing.bedrooms}
              </span>
            )}
            {listing.bathrooms !== undefined && listing.bathrooms > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {listing.bathrooms}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="p-4">
        {/* Meta Info */}
        <div className="flex items-center gap-3 mb-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {timeAgo(listing.updated_at || listing.created_at)}
          </span>
          <span className="flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{getLocation()}</span>
          </span>
        </div>
        
        {/* Title */}
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-1">
          {listing.title}
        </h3>
        
        {/* Price & Rating */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
            {formatPrice()}
          </span>
          {listing.rating && listing.rating > 0 && (
            <span className="flex items-center gap-1 text-sm text-yellow-500">
              <Star className="w-4 h-4 fill-current" />
              {listing.rating} ({listing.reviews || 0})
            </span>
          )}
        </div>
        
        {/* Amenities */}
        <div className="flex flex-wrap gap-2 mb-3">
          {listing.amenities?.slice(0, 3).map((amenity) => {
            const Icon = amenityIcons[amenity] || Home;
            return (
              <span key={amenity} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <Icon className="w-3 h-3" />
                {amenity.length > 12 ? `${amenity.slice(0, 10)}...` : amenity}
              </span>
            );
          })}
          {listing.amenities?.length > 3 && (
            <span className="text-xs text-gray-500">
              +{listing.amenities.length - 3}
            </span>
          )}
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {listing.viewing_count > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {listing.viewing_count}
            </span>
          )}
          {listing.favorite_count > 0 && (
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {listing.favorite_count}
            </span>
          )}
          {listing.parking_spots > 0 && (
            <span className="flex items-center gap-1">
              <Car className="w-3 h-3" />
              {listing.parking_spots}
            </span>
          )}
        </div>
        
        {/* Status */}
        {listing.property_status === "available" && (
          <div className="pt-3 mt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                {getStatusText()}
              </span>
              {listing.utilities_included === 1 && (
                <span className="text-xs text-gray-500 ml-auto">Utilities included</span>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}